'use client'

import { addressFormatter } from '@repo/sdk/helpers'
import { useSuspenseQuery } from '@repo/sdk/tanstack'
import { upfetch } from '@repo/sdk/upfetch'
import {
  GetAddressComponentSchema,
  GetAddressSchema,
  GetDropSchema,
  GetPlaceAddressBookSchema,
  GetPlaceSchema,
  z,
} from '@repo/sdk/zod'
import { cn } from '@repo/ui/lib'
import { Suspense, useRef } from 'react'

import { Skeleton } from '../../index.ts'

const _schema = GetPlaceSchema.extend({
  address_book: GetPlaceAddressBookSchema.nullish(),
})

interface LocationProps {
  data: z.infer<typeof GetDropSchema> & {
    place: z.infer<typeof _schema>
  }
  location: 'destination' | 'origin'
  status: 'cancelled' | string
}

export const LocationCell = (props: LocationProps) => {
  return (
    <Suspense
      fallback={
        <div className="flex flex-col items-start gap-1">
          <Skeleton className="w-full" />
          <Skeleton className="w-full" />
        </div>
      }
    >
      <LocationCellLoader {...props} />
    </Suspense>
  )
}

const LocationCellLoader = ({ data, status }: LocationProps) => {
  const { data: addressData } = useSuspenseQuery({
    async queryFn() {
      try {
        return await upfetch(`https://geolocation.cargoking.co.uk/google/place/details`, {
          headers: {
            'App-source': undefined,
          },
          params: {
            place_id: data?.place.place_id,
          },
          schema: GetAddressSchema.extend({
            address_components: GetAddressComponentSchema.array(),
          }),
        })
      } catch {
        return null
      }
    },
    queryKey: ['google/place/details', data],
  })
  const formattedAddress = addressFormatter(addressData?.address_components)

  const addressBookName = data.place?.address_book?.name
  const locationName =
    formattedAddress?.city ?? formattedAddress?.neighborhood ?? data.place?.city ?? ''
  const countryCode = formattedAddress?.countryCode ?? data.place?.country_code

  const placeName = addressBookName
  const address = locationName
  const postcode = formattedAddress?.postcode ?? data.place?.postcode

  const textRef = useRef<HTMLDivElement>(null)

  return (
    <div className="flex flex-col items-start gap-1">
      {placeName && (
        <p
          className={cn(
            'w-full truncate text-left text-xs text-[#637381] dark:text-[#d4d9de]',
            status === 'cancelled' && 'text-[#C3C3C3]',
          )}
          ref={textRef}
        >
          {placeName}
        </p>
      )}
      <div
        className={cn(
          'flex w-full items-center gap-1 truncate font-medium text-[#212B36] dark:text-[#b6c4d2]',
          status === 'cancelled' && 'text-[#C3C3C3]',
        )}
      >
        <p className="truncate text-left text-sm text-ellipsis whitespace-nowrap">
          {address}, {countryCode}
        </p>
      </div>
      {postcode && (
        <p
          className={cn(
            'w-full truncate text-left text-xs whitespace-nowrap text-[#637381] dark:text-[#d4d9de]',
            status === 'cancelled' && 'text-[#C3C3C3]',
          )}
        >
          {postcode}
        </p>
      )}
    </div>
  )
}
